import { Formik, FormikState } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';

import UserTable from './UserTable';

import router from '@/configs/react-router';
import useCreateGlobalNotificaiton from '@/hooks/globalNotification/userCreateGlobalNotification';
import { IGlobalNotification } from '@/interfaces/entities/global-notification';
import { notificationService } from '@/services/notification.service';

import NotificationDatePicker from '@components/global-notification/NotificationDatePicker.tsx';
import { formatDateForDisplay } from '@components/utils/timeFormatter.ts';

type IResetForm = (
	nextState?:
		| Partial<
				FormikState<{
					message: string;
					scheduledAt: Date;
					targetNotificationUsersIds: never[];
				}>
		  >
		| undefined,
) => void;

const GlobalNotification = () => {
	const createGlobalNotification = useCreateGlobalNotificaiton();
	const [changeDate, setChangeDate] = useState(false);
	const [selectedDateTime, setSelectedDateTime] = useState<string>('');
	const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
	const [usersError, setUsersError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [allUsersAreSelected, setAllUsersAreSelected] = useState(false);

	const initialValues = {
		message: '',
		scheduledAt: new Date(),
		targetNotificationUsersIds: [],
	};

	const handleDateTimeChange = (date: Date | null) => {
		if (date) {
			setSelectedDateTime(date.toISOString());
			setChangeDate(true);
		}
	};
	const handleSelect = () => {
		return selectedDateTime ? new Date(selectedDateTime) : new Date();
	};

	const handleReset = (resetForm: IResetForm) => {
		setLoading(true);
		resetForm();
		setSelectedDateTime('');
		setChangeDate(false);
		setLoading(false);
	};

	const validationSchema = Yup.object().shape({
		message: Yup.string().required('Message is required'),
	});

	const submitNewNotification = (
		handleSubmit: () => void,
		resetForm: IResetForm,
	) => {
		setLoading(true);
		handleSubmit();
		setLoading(false);
		resetForm();
		router.navigate('/notifications');
	};

	return (
		<div
			data-test="send-notification-modal"
			className="h-[100%] w-[75%] border-b border-b-gray-500 "
		>
			<Formik
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={async (values) => {
					const notificationValues: IGlobalNotification = {
						message: values.message,
						scheduledAt: changeDate ? new Date(selectedDateTime) : new Date(),
						targetNotificationUsersIds: selectedUsers,
					};
					try {
						if (!usersError) {
							createGlobalNotification.mutate(notificationValues);
							notificationService.success('Notification created successfully');
						} else {
							notificationService.error('Please select users to notify');
						}
					} catch (error) {
						throw new Error('Error sending notification:');
					}
				}}
			>
				{({
					handleChange,
					handleBlur,
					handleSubmit,
					values,
					errors,
					touched,
					resetForm,
				}) => (
					<div className={`flex-1 justify-center items-center p-4`}>
						<h1 className={`text-2xl text-center font-bold  mb-2 `}>
							New Global Notification
						</h1>
						<div className="h-full w-full flex flex-col items-center">
							<UserTable
								selectedUsers={selectedUsers}
								setSelectedUsers={setSelectedUsers}
								usersError={usersError}
								setUsersError={setUsersError}
								allUsersAreSelected={allUsersAreSelected}
								setAllUsersAreSelected={setAllUsersAreSelected}
							/>
						</div>
						<h2 className={`py-2 font-semibold`}>Message:</h2>
						<textarea
							data-test="message-input"
							className={`w-full bg-white border border-gray-300 rounded-md p-2 resize-none overflow-y-auto`}
							placeholder="Enter a message"
							onChange={handleChange}
							onBlur={handleBlur}
							value={values.message}
							name="message"
							rows={2}
						/>
						{touched.message && errors.message && (
							<p className={`text-red-500 text-sm`}>{errors.message}</p>
						)}
						<div className="flex justify-center items-center gap-4 w-full">
							<div className="w-full flex items-center justify-start">
								<h2 data-test="date-and-time" className={`py-2 font-semibold`}>
									Date & Time:
								</h2>
								<NotificationDatePicker
									selected={handleSelect()}
									onChange={handleDateTimeChange}
									minDate={new Date()}
								/>
							</div>
							<div className="flex justify-start  w-full">
								<h2
									data-test="scheduled-at"
									className={`font-semibold justify-start`}
								>
									Scheduled at:
								</h2>
								{changeDate ? (
									<h3 className="ml-2">
										{formatDateForDisplay(new Date(selectedDateTime))}
									</h3>
								) : (
									<h3 className="ml-2">Right now</h3>
								)}
							</div>
						</div>
						<div className="flex justify-end items-center w-full mt-4">
							<button
								data-test="reset-notification-button"
								className="bg-red-600 hover:bg-red-500 items-center justify-center px-3 py-1 rounded-md my-2"
								onClick={() => handleReset(resetForm)}
							>
								<p className="text-white text-center">RESET</p>
							</button>
							<button
								data-test="create-notification-button"
								className="bg-blue-600 hover:bg-blue-500 items-center justify-center px-3 py-1 rounded-md mx-4 ml-2 disabled:opacity-50"
								type="submit"
								disabled={loading}
								onClick={() => submitNewNotification(handleSubmit, resetForm)}
							>
								{changeDate ? (
									<p className={`text-white text-center`}>SEND LATER</p>
								) : (
									<p className={`text-white text-center`}>SEND NOW</p>
								)}
							</button>
						</div>
					</div>
				)}
			</Formik>
		</div>
	);
};

export default GlobalNotification;
